import React, { useState, useEffect, useRef } from 'react';

export const MultiSelectDropdown = ({ options, selected, onChange, placeholder, language, optionKey = 'state' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt => 
    (opt[optionKey] || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleItem = (item) => {
    const value = item[optionKey];
    if (selected.includes(value)) {
      onChange(selected.filter(s => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const selectAll = () => {
    onChange(options.map(o => o[optionKey]));
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectPendingOnly = () => {
    onChange(options.filter(o => o.pending > 0).map(o => o[optionKey]));
  };

  const totalPending = selected.length > 0 
    ? options.filter(o => selected.includes(o[optionKey])).reduce((sum, o) => sum + (o.pending || 0), 0)
    : options.reduce((sum, o) => sum + (o.pending || 0), 0);

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-2 border border-purple-300 rounded-lg bg-white cursor-pointer flex justify-between items-center min-w-[320px] hover:border-purple-500 transition-colors"
        data-testid={`multi-select-${optionKey}-dropdown`}
      >
        <span className="text-sm truncate">
          {selected.length === 0 ? (
            <span className="text-gray-500">{placeholder}</span>
          ) : selected.length === 1 ? (
            selected[0]
          ) : (
            <span className="text-purple-700 font-medium">{selected.length} {language === 'hi' ? (optionKey === 'district' ? 'जिले चुने गए' : 'राज्य चुने गए') : `${optionKey}s selected`}</span>
          )}
        </span>
        <span className={`ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b bg-gray-50">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={language === 'hi' ? `🔍 ${optionKey === 'district' ? 'जिला' : 'State'} खोजें...` : `🔍 Search ${optionKey}...`}
              className="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              onClick={(e) => e.stopPropagation()}
              data-testid={`${optionKey}-search-input`}
            />
          </div>

          {/* Quick Actions */}
          <div className="p-2 border-b bg-purple-50 flex gap-2 flex-wrap">
            <button 
              onClick={(e) => { e.stopPropagation(); selectPendingOnly(); }}
              className="px-2 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600"
              data-testid={`select-pending-${optionKey}-btn`}
            >
              ⏳ {language === 'hi' ? 'Pending वाले' : 'Pending Only'}
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); selectAll(); }}
              className="px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
              data-testid={`select-all-${optionKey}-btn`}
            >
              ✅ {language === 'hi' ? 'सभी चुनें' : 'Select All'}
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); clearAll(); }}
              className="px-2 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
              data-testid={`clear-all-${optionKey}-btn`}
            >
              ✖️ {language === 'hi' ? 'Clear' : 'Clear'}
            </button>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.map(opt => {
              const value = opt[optionKey];
              const isSelected = selected.includes(value);
              const isComplete = opt.pending === 0;
              return (
                <div
                  key={value}
                  onClick={(e) => { e.stopPropagation(); toggleItem(opt); }}
                  className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-purple-50 border-b border-gray-50 ${isSelected ? 'bg-purple-100' : ''} ${isComplete ? 'opacity-60' : ''}`}
                  data-testid={`${optionKey}-option-${value.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className={`text-sm ${isComplete ? 'text-green-600' : 'text-gray-800'}`}>
                      {isComplete ? '✅ ' : ''}{value}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    <span className="text-green-600">{opt.enriched || 0}</span>
                    <span className="mx-1">/</span>
                    <span>{opt.total || 0}</span>
                    {opt.pending > 0 && (
                      <span className="ml-1 text-orange-600 font-medium">({opt.pending}⏳)</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Footer */}
          <div className="p-2 border-t bg-gray-50 text-center">
            <span className="text-xs text-gray-600">
              {language === 'hi' ? 'Selected में कुल pending:' : 'Total pending in selected:'} 
              <strong className="text-orange-600 ml-1">{totalPending.toLocaleString()}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
